import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getChannels } from '../../api/client';
import { ErrorState } from '../../components/ErrorState';
import { Spinner } from '../../components/Spinner';
import { useAppSettings } from '../../hooks/useAppSettings';
import { getSdkSearchParams, getVirtualChannels } from '../../lib/settings';
import type { MediaTailorSession } from '../../lib/mediaTailor';
import { loadSdk } from '../../lib/sdk';
import type { Channel, ItgConfig } from '../../types/app';
import { BitmovinPlayer } from './BitmovinPlayer';
import { NativePlayer } from './NativePlayer';
import { ShakaPlayer } from './ShakaPlayer';
import { VideoJsAdapter } from './VideoJsAdapter';

interface ChannelLocationState {
  channel?: Channel;
}

const isChannel = (value: unknown): value is Channel => {
  if (!value || typeof value !== 'object') return false;
  const channel = value as Partial<Channel>;
  return (
    typeof channel.label === 'string' &&
    typeof channel.slug === 'string' &&
    typeof channel.video === 'string'
  );
};

export const ChannelPage = () => {
  const {
    env: environment = 'dev',
    accountId = '',
    channelId = '',
  } = useParams();
  const location = useLocation();
  const { device, isTv, playerType } = useAppSettings();
  const initialChannel = useMemo(() => {
    const locationState = location.state as ChannelLocationState | null;
    return isChannel(locationState?.channel) &&
      locationState.channel.slug === channelId
      ? locationState.channel
      : null;
  }, [channelId, location.state]);

  const [channel, setChannel] = useState<Channel | null>(initialChannel);
  const [readyLoadKey, setReadyLoadKey] = useState('');
  const [readyPlayerKey, setReadyPlayerKey] = useState('');
  const [mediaTailorSession, setMediaTailorSession] =
    useState<MediaTailorSession>();
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const loadKey = `${environment}:${accountId}:${channelId}:${String(isTv)}:${attempt}`;
  const sdkReady = readyLoadKey === loadKey;
  const playerKey = `${channel?.slug || channelId}:${playerType}`;
  const playerReady = readyPlayerKey === playerKey;

  useEffect(() => {
    let cancelled = false;

    const channelRequest = initialChannel
      ? Promise.resolve(initialChannel)
      : getChannels(accountId, environment).then((response) => {
          const match = response.channels.find(
            (item) => item.slug === channelId,
          );
          if (!match) throw new Error('The requested channel was not found');
          return match;
        });

    Promise.all([loadSdk(environment), channelRequest])
      .then(([, loadedChannel]) => {
        if (cancelled) return;
        setChannel(loadedChannel);
        setReadyLoadKey(loadKey);
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setError(
            reason instanceof Error ? reason.message : 'Unable to load channel',
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accountId, channelId, environment, initialChannel, isTv, loadKey]);

  const handlePlayerReady = useCallback(
    (session?: MediaTailorSession) => {
      setMediaTailorSession(session);
      setReadyPlayerKey(playerKey);
    },
    [playerKey],
  );

  useEffect(() => {
    if (!channel || !sdkReady || !playerReady || !window.inthegame) return;

    const config: ItgConfig = {
      ...getSdkSearchParams(location.search),
      accountId,
      channelSlug: channel.slug,
      device,
      env: environment,
      videoPlayerId:
        playerType === 'videojs' ? 'itg-player_html5_api' : 'itg-player',
      virtualChannels: getVirtualChannels(location.search),
      // flexiConfig:
      //   playerType === 'videojs'
      //     ? { playerNodeMapping: { videoNodeId: 'itg-player' } }
      //     : undefined,
      palConfig: {
        enable: true,
        adWillAutoPlay: false,
        adWillPlayMuted: false,
        descriptionUrl: window.location.origin,
        url: window.location.href,
      },
      vars: {
        sourceApp: 'ott',
        ottAccountId: accountId,
        ottDevice: device,
        ottPlayerType: playerType,
        ottSlug: channel.slug,
        descriptionUrl: window.location.origin,
        url: window.location.href,
      },
    };

    window.inthegame.init(config);

    if (playerType === 'shaka-mt' && mediaTailorSession) {
      window.inthegame.startMediaTailor?.({
        trackingUrl: mediaTailorSession.trackingUrl,
        disableResourceTypeCheck: true,
        injectImmediately: true,
      });
    }

    return () => {
      try {
        window.inthegame?.stop();
      } catch {
        // The external SDK may already be stopped during player replacement.
      }
    };
  }, [
    accountId,
    channel,
    device,
    environment,
    location.search,
    mediaTailorSession,
    playerReady,
    playerType,
    sdkReady,
  ]);

  if (error) {
    return (
      <ErrorState
        message={error}
        onRetry={() => {
          setError(null);
          setAttempt((value) => value + 1);
        }}
      />
    );
  }

  if (!channel || !sdkReady) {
    return (
      <div className="player-surface flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <main
      className="player-surface relative flex h-full w-full items-center justify-center overflow-hidden"
      data-player-root
    >
      {playerType === 'videojs' ? (
        <VideoJsAdapter
          key={playerKey}
          src={channel.video}
          onReady={handlePlayerReady}
        />
      ) : playerType === 'bitmovin' ? (
        <BitmovinPlayer
          key={playerKey}
          src={channel.video}
          onReady={handlePlayerReady}
        />
      ) : playerType === 'shaka' || playerType === 'shaka-mt' ? (
        <ShakaPlayer
          key={playerKey}
          src={channel.video}
          mediaTailor={playerType === 'shaka-mt'}
          onReady={handlePlayerReady}
        />
      ) : (
        <NativePlayer
          key={playerKey}
          src={channel.video}
          onReady={handlePlayerReady}
        />
      )}
    </main>
  );
};
